import { z } from 'zod'
import type { CrudField, CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'

type Translate = (key: string, fallback: string, params?: Record<string, string | number>) => string

export type SlotFormValues = {
  locationId?: string
  slotType?: string
  startTime?: string
  endTime?: string
  cargoId?: string
}

export type SlotLocationOption = {
  value: string
  label: string
}

export type SlotCargoOption = {
  value: string
  label: string
}

const toNullableText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function buildSlotFormSchema(t: Translate) {
  return z
    .object({
      locationId: z.string().optional(),
      slotType: z.string().optional(),
      startTime: z.string().optional(),
      endTime: z.string().optional(),
      cargoId: z.string().optional(),
    })
    .superRefine((values, ctx) => {
      if (!toNullableText(values.locationId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['locationId'],
          message: t('slots.form.errors.locationId', 'Location is required.'),
        })
      }
      if (!toNullableText(values.slotType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['slotType'],
          message: t('slots.form.errors.slotType', 'Slot type is required.'),
        })
      }
      const startTime = toNullableText(values.startTime)
      const endTime = toNullableText(values.endTime)
      if (!startTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['startTime'],
          message: t('slots.form.errors.startTime', 'Start time is required.'),
        })
      }
      if (!endTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endTime'],
          message: t('slots.form.errors.endTime', 'End time is required.'),
        })
      }
      if (startTime && endTime) {
        const start = new Date(startTime)
        const end = new Date(endTime)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endTime'],
            message: t('slots.form.errors.range', 'End time must be later than start time.'),
          })
        }
      }
    })
}

export function buildSlotFields(
  t: Translate,
  options: {
    loadLocations: (query?: string) => Promise<SlotLocationOption[]>
    loadCargo: (query?: string) => Promise<SlotCargoOption[]>
  },
): CrudField[] {
  return [
    {
      id: 'locationId',
      label: t('slots.form.fields.locationId', 'Location'),
      type: 'combobox',
      placeholder: t('slots.form.fields.locationIdPlaceholder', 'Search by location name or code'),
      description: t(
        'slots.form.fields.locationIdHelp',
        'Search active slot-enabled locations by name or code.',
      ),
      loadOptions: options.loadLocations,
      allowCustomValues: false,
      required: true,
    },
    {
      id: 'slotType',
      label: t('slots.form.fields.slotType', 'Slot type'),
      type: 'select',
      required: true,
      options: [
        { value: 'LOADING', label: t('slots.types.LOADING', 'Loading') },
        { value: 'UNLOADING', label: t('slots.types.UNLOADING', 'Unloading') },
        { value: 'STORAGE', label: t('slots.types.STORAGE', 'Storage') },
        { value: 'TRANSIT', label: t('slots.types.TRANSIT', 'Transit') },
      ],
    },
    {
      id: 'startTime',
      label: t('slots.form.fields.startTime', 'Start time'),
      type: 'datetime-local',
      required: true,
    },
    {
      id: 'endTime',
      label: t('slots.form.fields.endTime', 'End time'),
      type: 'datetime-local',
      required: true,
    },
    {
      id: 'cargoId',
      label: t('slots.form.fields.cargoId', 'Cargo'),
      type: 'combobox',
      placeholder: t('slots.form.fields.cargoIdPlaceholder', 'Search cargo by reference, container, or SSCC'),
      description: t(
        'slots.form.fields.cargoIdHelp',
        'Optional. Search cargo records by reference number, container number, or SSCC.',
      ),
      loadOptions: async (query?: string) => {
        const results = await options.loadCargo(query)
        return [{ value: '', label: t('slots.form.fields.cargoIdEmpty', 'No cargo link') }, ...results]
      },
      allowCustomValues: false,
    },
  ]
}

export function buildSlotGroups(t: Translate): CrudFormGroup[] {
  return [
    {
      id: 'slot-booking',
      title: t('slots.form.groups.booking', 'Slot booking'),
      fields: ['locationId', 'slotType', 'startTime', 'endTime', 'cargoId'],
    },
  ]
}

function toIsoDateTime(value: string | undefined): string | null {
  const trimmed = toNullableText(value)
  if (!trimmed) return null
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toISOString()
}

export function buildSlotPayload(values: SlotFormValues): Record<string, unknown> {
  return {
    locationId: toNullableText(values.locationId),
    slotType: toNullableText(values.slotType),
    startTime: toIsoDateTime(values.startTime),
    endTime: toIsoDateTime(values.endTime),
    cargoId: toNullableText(values.cargoId),
  }
}

export function buildInitialSlotValues(): SlotFormValues {
  const start = new Date()
  start.setMinutes(0, 0, 0)
  start.setHours(start.getHours() + 1)
  const end = new Date(start)
  end.setHours(end.getHours() + 1)

  return {
    slotType: 'LOADING',
    startTime: start.toISOString().slice(0, 16),
    endTime: end.toISOString().slice(0, 16),
    cargoId: '',
  }
}
